import {
    useEffect,
    useState,
    Fragment
} from "react";
import {
    TouchableOpacity,
    View
} from "react-native";
import type PaginationProps from "./type";
import type {
    PaginationButtonType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitTheme,
    Button,
    Text
} from "ncore-ui-kit";
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    ChevronsRight,
    ChevronsLeft
} from "lucide-react-native";

const Pagination = ({
    totalDataCount,
    currentPage,
    itemPerPage,
    onSelect,
    style
}: PaginationProps) => {
    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        buttons,
        setButtons
    ] = useState<PaginationButtonType[]>([
        {
            pageNumber: 1
        }
    ]);

    const calculateButtons = () => {
        const pageCount = Math.ceil(totalDataCount / itemPerPage);

        if (pageCount > 7) {
            let _pageButtons: PaginationButtonType[] = [];

            for (let i = 0; i < 3; i++) {
                _pageButtons.push({
                    pageNumber: i + 1
                });
            }

            for (let i = currentPage - 1; i < currentPage + 2; i++) {
                const isExists = _pageButtons.findIndex(item => item.pageNumber === i);

                if (
                    isExists === -1 &&
                    i < pageCount &&
                    (_pageButtons[_pageButtons.length - 1] as PaginationButtonType).pageNumber < i
                ) {
                    _pageButtons.push({
                        pageNumber: i
                    });
                }
            }

            for (let i = pageCount - 2; i < pageCount + 1; i++) {
                const isExists = _pageButtons.findIndex(item => item.pageNumber === i);

                if (isExists === -1) {
                    _pageButtons.push({
                        pageNumber: i
                    });
                }
            }

            _pageButtons = _pageButtons.sort((a, b) => a.pageNumber - b.pageNumber);
            setButtons(_pageButtons);
        } else if (pageCount === 0) {
            setButtons([]);
            return;
        } else {
            const _pageButtons: PaginationButtonType[] = [];
            for (let i = 0; i < pageCount; i++) {
                _pageButtons.push({
                    pageNumber: i + 1
                });
            }
            setButtons(_pageButtons);
        }
    };

    useEffect(() => {
        calculateButtons();
    }, [
        totalDataCount,
        itemPerPage,
        currentPage
    ]);

    const renderButtons = () => {
        if (!buttons || !buttons.length) {
            return null;
        }

        return buttons.map((item, index) => {
            const prevButton = buttons[index - 1] as PaginationButtonType | undefined;
            const showDots = prevButton && item.pageNumber - 1 !== prevButton.pageNumber;

            return <Fragment
                key={`page-button-${item.pageNumber}`}
            >
                {
                    showDots ?
                        <View
                            style={[
                                stylesheet.dotsContainer,
                                {
                                    marginHorizontal: spaces.spacingXs
                                }
                            ]}
                        >
                            <Text
                                style={[
                                    stylesheet.dotsText,
                                    {
                                        color: colors.content.text.mid
                                    }
                                ]}
                                variant="bodyMediumSize"
                            >
                                ...
                            </Text>
                        </View>
                    :
                        null
                }
                <View
                    style={[
                        stylesheet.buttonWrapper,
                        {
                            marginHorizontal: spaces.spacingXs
                        }
                    ]}
                >
                    <Button
                        customBorderColor={item.pageNumber === currentPage ? "gold" : "brandWhite"}
                        customColor={item.pageNumber === currentPage ? "gold" : "brandWhite"}
                        customTextColor={item.pageNumber === currentPage ? "white" : "black"}
                        title={String(item.pageNumber)}
                        onPress={() => {
                            onSelect(item, index);
                        }}
                        spreadBehaviour="stretch"
                    />
                </View>
            </Fragment>;
        });
    };

    if (buttons.length === 0) {
        return null;
    }

    const firstButton = buttons[0] as PaginationButtonType;
    const lastButton = buttons[buttons.length - 1] as PaginationButtonType;

    const pageCount = Math.ceil(totalDataCount / itemPerPage);
    const showNavigationArrows = pageCount > 7;

    return <View
        style={[
            stylesheet.container,
            {
                gap: spaces.spacingSm,
                ...style
            }
        ]}
    >
        {
            showNavigationArrows ?
                <Fragment>
                    <TouchableOpacity
                        style={[
                            stylesheet.buttonWrapper,
                            {
                                opacity: currentPage === firstButton.pageNumber ? 0.5 : 1,
                                marginHorizontal: spaces.spacingXs,
                                justifyContent: "center",
                                alignItems: "center"
                            }
                        ]}
                        disabled={currentPage === firstButton.pageNumber}
                        onPress={() => {
                            onSelect(firstButton, 0);
                        }}
                    >
                        <ChevronsLeft
                            color={currentPage === firstButton.pageNumber ? colors.content.icon.disabled : colors.content.icon.mid}
                            size={20}
                        />
                    </TouchableOpacity>
                    <TouchableOpacity
                        onPress={() => {
                            const currentIndex = buttons.findIndex(item => item.pageNumber === currentPage);
                            const targetButton = buttons[currentIndex - 1] as PaginationButtonType | undefined;

                            if (targetButton) {
                                onSelect(targetButton, currentIndex - 1);
                            } else {
                                onSelect(firstButton, 0);
                            }
                        }}
                        style={[
                            stylesheet.buttonWrapper,
                            {
                                opacity: currentPage === firstButton.pageNumber ? 0.5 : 1,
                                marginHorizontal: spaces.spacingXs,
                                justifyContent: "center",
                                alignItems: "center"
                            }
                        ]}
                        disabled={currentPage === firstButton.pageNumber}
                    >
                        <ChevronLeftIcon
                            color={currentPage === firstButton.pageNumber ? colors.content.icon.disabled : colors.content.icon.mid}
                            size={20}
                        />
                    </TouchableOpacity>
                </Fragment>
            :
                null
        }
        <View
            style={[
                stylesheet.buttonsContainer,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            {renderButtons()}
        </View>
        {
            showNavigationArrows ?
                <Fragment>
                    <TouchableOpacity
                        onPress={() => {
                            const currentIndex = buttons.findIndex(item => item.pageNumber === currentPage);
                            const targetButton = buttons[currentIndex + 1] as PaginationButtonType | undefined;

                            if (targetButton) {
                                onSelect(targetButton, currentIndex + 1);
                            } else {
                                onSelect(lastButton, buttons.length - 1);
                            }
                        }}
                        style={[
                            stylesheet.buttonWrapper,
                            {
                                opacity: currentPage === lastButton.pageNumber ? 0.5 : 1,
                                marginHorizontal: spaces.spacingXs,
                                justifyContent: "center",
                                alignItems: "center"
                            }
                        ]}
                        disabled={currentPage === lastButton.pageNumber}
                    >
                        <ChevronRightIcon
                            color={currentPage === lastButton.pageNumber ? colors.content.icon.disabled : colors.content.icon.mid}
                            size={20}
                        />
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[
                            stylesheet.buttonWrapper,
                            {
                                opacity: currentPage === lastButton.pageNumber ? 0.5 : 1,
                                marginHorizontal: spaces.spacingXs,
                                justifyContent: "center",
                                alignItems: "center"
                            }
                        ]}
                        disabled={currentPage === lastButton.pageNumber}
                        onPress={() => {
                            onSelect(lastButton, buttons.length - 1);
                        }}
                    >
                        <ChevronsRight
                            color={currentPage === lastButton.pageNumber ? colors.content.icon.disabled : colors.content.icon.mid}
                            size={20}
                        />
                    </TouchableOpacity>
                </Fragment>
            :
                null
        }
    </View>;
};

export default Pagination;
